import React, { ChangeEvent, useState } from 'react';

import { getRoutesDirections } from '@/data/libraries/Routes';

import { NavigateFunction, useLocation, useNavigate, type Location } from 'react-router-dom';

import { ScreenSize, useScreenSize } from '@/ui/hooks/useScreenSize';
import useScrollToTop from '@/ui/hooks/useScrollToTop';

import { Button, Col, Container, Image, Row } from 'react-bootstrap';

import OrdersController from '@/controllers/OrdersController';

import { IBreadcrumbs } from '@/domain/interfaces/IBreadcrumbs';
import { IOrderData } from '@/domain/interfaces/IOrder';

import lastPageName from '@/domain/utils/lastPageName';
import { translate } from '@/domain/utils/translate';
import ErrorImage from '@/ui/assets/ErrorImage';

import Breadcrumbs from '@/ui/components/Breadcrumbs';
import InputForm from '@/ui/components/forms/InputForm';

import TrackingOrderDetails from '@/ui/pages/sales/TrackingOrderDetails/index';

/**
 * Página de búsqueda de ordenes
 * @returns {JSX.Element} Página de búsqueda de ordenes
 */
const SearchOrder = (): JSX.Element => {
  /** VARIABLES DE ENTORNO */
  const URL_IMAGES: string = process.env.REACT_APP_URL_IMAGES ?? '';

  /** Hook para trabajar con document location */
  const location: Location = useLocation();

  /** 
   * Usar el hook mejorado de scroll to top
   * Esta implementación utiliza múltiples técnicas para garantizar que el scroll funcione
   */
  useScrollToTop({
    scrollOnMount: true,
    scrollOnLoad: true
  });

  /** Controlador para obtener las órdenes */
  const { getOrdersByCode } = OrdersController();

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /** Listado de breadcrumbs de la página */
  const breadcrumbsList: IBreadcrumbs[] = [
    { label: translate('breadcrumbs.home'), url: routesDirections.MAIN_ROUTE },
    { label: lastPageName(location.pathname), url: location.pathname },
  ];

  /** Dimensiones de la pantalla */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  const isMobile: boolean = width < maxLargeWidth;

  /** Navegación */
  const navigate: NavigateFunction = useNavigate();

  /** Estados */
  const [inputValue, setInputValue] = useState<string>('');
  const [descriptionInputWithError, setDescriptionInputWithError] = useState<boolean>(false);
  const [descriptionMessageError, setDescriptionMessageError] = useState<string | undefined>('');

  /** Valores constantes */
  const TRACKING_IMAGE_URL = `${URL_IMAGES}/client/seguimiento.png`;
  const TRACKING_TITLE_TEXT = translate('sales.purchase_tracking_title');
  const TRACKING_DESCRIPTION_TEXT = translate('sales.purchase_description');
  const SEARCH_BUTTON_TEXT = translate('sales.purchase_search_button_text');

  /** Objeto de orden (null por defecto) */
  const order: IOrderData | null = null;

  /**
   * Actualiza el valor del input y resetea errores
   * @param e Evento del cambio de input
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setDescriptionInputWithError(false);
    setInputValue(e.target.value.toUpperCase());
  };

  /**
   * Busca la orden por código y navega al detalle si existe
   */
  const getOrdersBack = async (): Promise<void> => {
    const result = await getOrdersByCode(inputValue);

    if (!result) {
      setDescriptionMessageError(translate('sales.incorrect_id_warning'));
      setDescriptionInputWithError(true);
      return;
    }

    navigate(routesDirections.TRACKING_ROUTE_DETAIL, { state: { order: result } });
  };

  /** Renderizado condicional basado en si hay una orden */
  return order ? (
    <TrackingOrderDetails />
  ) : (
    <Container fluid className='bg-primary-5 p-0'>
      <Container className={`p-0 bg-primary-5 ${isMobile ? 'pb-2' : 'pb-5'}`}>
        <Row className='g-0 pt-2'>
          <Col lg={12} className={`p-2 bg-secondary-5 shadow ${!isMobile ? 'rounded' : ''}`}>
            <Breadcrumbs list={breadcrumbsList} />
          </Col>
        </Row>
        <Row className={`g-0 bg-secondary-5 shadow ${!isMobile ? 'rounded' : ''}  mt-2 mt-lg-4`}>
          <Col className='text-center'>
            <h2 className='text-secondary-1 text-center my-3'>{TRACKING_TITLE_TEXT}</h2>
            <Image
              src={TRACKING_IMAGE_URL}
              alt='order traking'
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = ErrorImage;
              }}
            />
            <Col xs={11} lg={4} className='px-lg-4 my-4 mx-auto text-justify'>
              {TRACKING_DESCRIPTION_TEXT}
              <Col className='mt-4'>
                <InputForm
                  iconName='ico-error'
                  label={translate('sales.purchase_id')}
                  classLabel='text-secondary-2 p-regular '
                  type='text'
                  classMain='text-secondary-1 fw-bold mb-3 text-uppercase'
                  className='bg-secondary-5 '
                  withErrors={descriptionInputWithError}
                  messageError={descriptionMessageError}
                  onChange={handleInputChange}
                />
                <Button
                  className={`w-100 svg-primary-4 btn-primary-icon-standard ${inputValue.length < 1 ? 'disabled' : ''} `}
                  onClick={getOrdersBack}>
                  {SEARCH_BUTTON_TEXT}
                </Button>
              </Col>
            </Col>
          </Col>
        </Row>
      </Container>
    </Container>
  );
};

export default SearchOrder;
